// frontend/src/components/AuditorAccountManager.js
// Superuser-only UI for creating, listing, and revoking auditor accounts.

import React, { useState, useEffect, useCallback } from 'react';

const fmtDate = (v) => v ? new Date(v).toLocaleString() : '—';

export default function AuditorAccountManager({ authAxios, showFormMessage, firms }) {
  const [auditors, setAuditors] = useState([]);
  const [loading, setLoading] = useState(true);
  const [creating, setCreating] = useState(false);
  const [revoking, setRevoking] = useState(null);

  // Create form
  const [formData, setFormData] = useState({
    first_name: '',
    last_name: '',
    email: '',
    firm_id: '',
    expiry_days: 7,
  });

  const fetchAuditors = useCallback(async () => {
    try {
      const resp = await authAxios.get('auth/list-auditors/');
      setAuditors(resp.data || []);
    } catch (err) {
      console.error('Failed to fetch auditors:', err);
    }
    setLoading(false);
  }, [authAxios]);

  useEffect(() => { fetchAuditors(); }, [fetchAuditors]);

  const handleCreate = async (e) => {
    e.preventDefault();
    if (!formData.email || !formData.firm_id) {
      showFormMessage('danger', 'Email and Firm are required.');
      return;
    }
    setCreating(true);
    try {
      const resp = await authAxios.post('auth/create-auditor/', formData);
      showFormMessage('success', `Auditor account created for ${resp.data.email}. A set-password link has been generated.`);
      setFormData({ first_name: '', last_name: '', email: '', firm_id: '', expiry_days: 7 });
      fetchAuditors();
    } catch (err) {
      const msg = err.response?.data?.error || err.response?.data?.detail || 'Failed to create auditor account.';
      showFormMessage('danger', msg);
    }
    setCreating(false);
  };

  const handleRevoke = async (userId) => {
    setRevoking(userId);
    try {
      await authAxios.post('auth/revoke-auditor/', { user_id: userId });
      showFormMessage('success', 'Auditor access revoked.');
      fetchAuditors();
    } catch (err) {
      const msg = err.response?.data?.error || 'Failed to revoke auditor.';
      showFormMessage('danger', msg);
    }
    setRevoking(null);
  };

  return (
    <div className="space-y-8">
      <div>
        <h2 className="text-3xl font-bold text-gray-800">Manage Auditor Accounts</h2>
        <p className="text-gray-600 mt-1">Create time-limited read-only accounts for external auditors.</p>
      </div>

      {/* ── Create Form ─────────────────────────── */}
      <div className="bg-white rounded-xl shadow-sm border border-gray-100 p-6">
        <h3 className="text-lg font-semibold text-gray-800 mb-4">Create Auditor Account</h3>
        <form onSubmit={handleCreate} className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">First Name</label>
            <input
              type="text"
              value={formData.first_name}
              onChange={(e) => setFormData(f => ({ ...f, first_name: e.target.value }))}
              className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:ring-2 focus:ring-teal-500 focus:border-teal-500"
              placeholder="Jane"
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Last Name</label>
            <input
              type="text"
              value={formData.last_name}
              onChange={(e) => setFormData(f => ({ ...f, last_name: e.target.value }))}
              className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:ring-2 focus:ring-teal-500 focus:border-teal-500"
              placeholder="Doe"
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Email <span className="text-red-500">*</span></label>
            <input
              type="email"
              required
              value={formData.email}
              onChange={(e) => setFormData(f => ({ ...f, email: e.target.value }))}
              className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:ring-2 focus:ring-teal-500 focus:border-teal-500"
              placeholder="auditor@firm.com"
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Firm <span className="text-red-500">*</span></label>
            <select
              required
              value={formData.firm_id}
              onChange={(e) => setFormData(f => ({ ...f, firm_id: e.target.value }))}
              className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:ring-2 focus:ring-teal-500 focus:border-teal-500"
            >
              <option value="">Select firm…</option>
              {(firms || []).map(f => (
                <option key={f.id} value={f.id}>{f.firm_name}</option>
              ))}
            </select>
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Access Duration (days)</label>
            <input
              type="number"
              min="1"
              max="90"
              value={formData.expiry_days}
              onChange={(e) => setFormData(f => ({ ...f, expiry_days: parseInt(e.target.value) || 7 }))}
              className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:ring-2 focus:ring-teal-500 focus:border-teal-500"
            />
            <p className="text-xs text-gray-400 mt-1">Default: 7 days. Account auto-expires after this period.</p>
          </div>
          <div className="flex items-end">
            <button
              type="submit"
              disabled={creating}
              className="w-full px-4 py-2 bg-teal-600 text-white font-medium rounded-lg hover:bg-teal-700 disabled:opacity-50 transition-colors text-sm"
            >
              {creating ? 'Creating…' : 'Create Auditor Account'}
            </button>
          </div>
        </form>
      </div>

      {/* ── Auditors List ─────────────────────────── */}
      <div className="bg-white rounded-xl shadow-sm border border-gray-100 overflow-hidden">
        <div className="px-6 py-4 border-b border-gray-100 flex items-center justify-between">
          <h3 className="text-lg font-semibold text-gray-800">Auditor Accounts ({auditors.length})</h3>
          <button onClick={fetchAuditors} className="text-sm text-teal-600 hover:text-teal-800 transition-colors">
            Refresh
          </button>
        </div>

        {loading ? (
          <div className="flex justify-center py-12">
            <div className="animate-spin rounded-full h-8 w-8 border-t-2 border-b-2 border-teal-500"></div>
          </div>
        ) : auditors.length === 0 ? (
          <div className="px-6 py-12 text-center text-gray-500">
            No auditor accounts found. Use the form above to create one.
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead className="bg-gray-50 text-gray-600 uppercase text-xs">
                <tr>
                  <th className="px-4 py-3 text-left">Name</th>
                  <th className="px-4 py-3 text-left">Email</th>
                  <th className="px-4 py-3 text-left">Firm</th>
                  <th className="px-4 py-3 text-left">Status</th>
                  <th className="px-4 py-3 text-left">Created</th>
                  <th className="px-4 py-3 text-left">Expires</th>
                  <th className="px-4 py-3 text-left">Last Login</th>
                  <th className="px-4 py-3 text-center">Actions</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-100">
                {auditors.map((a) => {
                  const isExpired = a.account_expires_at && new Date(a.account_expires_at) < new Date();
                  const isActive = a.is_active && !isExpired;
                  return (
                    <tr key={a.user_id} className="hover:bg-gray-50 transition-colors">
                      <td className="px-4 py-3 text-gray-800 font-medium">
                        {a.first_name || ''} {a.last_name || ''}
                        {!a.first_name && !a.last_name && a.username}
                      </td>
                      <td className="px-4 py-3 text-gray-600">{a.email}</td>
                      <td className="px-4 py-3 text-gray-600">{a.firm_name || '—'}</td>
                      <td className="px-4 py-3">
                        <span className={`px-2 py-0.5 rounded-full text-xs font-medium ${
                          isActive ? 'bg-green-100 text-green-700' :
                          isExpired ? 'bg-amber-100 text-amber-700' :
                          'bg-red-100 text-red-700'
                        }`}>
                          {isActive ? 'Active' : isExpired ? 'Expired' : 'Revoked'}
                        </span>
                      </td>
                      <td className="px-4 py-3 text-gray-600 text-xs">{fmtDate(a.created_at || a.date_joined)}</td>
                      <td className="px-4 py-3 text-gray-600 text-xs">{fmtDate(a.account_expires_at)}</td>
                      <td className="px-4 py-3 text-gray-600 text-xs">{fmtDate(a.last_login)}</td>
                      <td className="px-4 py-3 text-center">
                        {isActive && (
                          <button
                            onClick={() => handleRevoke(a.user_id)}
                            disabled={revoking === a.user_id}
                            className="px-3 py-1 text-xs font-medium bg-red-50 text-red-600 rounded-lg hover:bg-red-100 disabled:opacity-50 transition-colors"
                          >
                            {revoking === a.user_id ? 'Revoking…' : 'Revoke'}
                          </button>
                        )}
                        {!isActive && (
                          <span className="text-xs text-gray-400">No actions</span>
                        )}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {/* Help text */}
      <div className="bg-blue-50 border border-blue-200 rounded-xl p-5">
        <h4 className="text-sm font-semibold text-blue-800 mb-2">How Auditor Accounts Work</h4>
        <ul className="text-sm text-blue-700 space-y-1 list-disc list-inside">
          <li>Created auditors receive a set-password link via the response.</li>
          <li>Once logged in, they see a dedicated read-only auditor portal with all firm data.</li>
          <li>Access is time-limited — accounts auto-expire after the specified number of days.</li>
          <li>You can revoke access at any time using the Revoke button.</li>
          <li>Auditors can only view and export data. No edits or deletions are possible.</li>
        </ul>
      </div>
    </div>
  );
}
